import React, { useState, useEffect } from 'react';
import {
    Sheet,
    Typography,
    FormControl,
    FormLabel,
    Input,
    Button,
    Stack,
    Alert,
    Box
} from '@mui/joy';
import { useNavigate } from 'react-router-dom';
import Logo from '../components/Logo';
import axiosConfig from '../util/axiosConfig';
import { clearStoredAuth } from '../util/authStorage';
import { usePageTitle } from '../util/usePageTitle';

const ForgotPassword = () => {
    const navigate = useNavigate();
    usePageTitle('Reset Password');

    useEffect(() => {
        // Clear any existing auth data when accessing forgot password
        clearStoredAuth();
    }, []);

    const [step, setStep] = useState(1);
    const [email, setEmail] = useState(''); // 1: Email, 2: OTP, 3: New Password
    const [studentId, setStudentId] = useState('');
    const [otp, setOtp] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleEmailSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsSubmitting(true);

        try {

            const response = await axiosConfig.post(`/auth/forgot-password/request`, { studentId, email });
            setSuccess(response.data.message);
            setStep(2);
        } catch (err) {
            setError(err.response?.data?.errorMessage || err.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleOtpSubmit = async (e) => {
        e.preventDefault();
        if (otp.length !== 6) {
            setError('Please enter a valid 6-digit OTP');
            return;
        }
        setStep(3);
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsSubmitting(true);

        try {
            if (newPassword !== confirmPassword) {
                throw new Error('Passwords do not match');
            }

            if (newPassword.length < 6) {
                throw new Error('Password must be at least 6 characters long');
            }

            await axiosConfig.post(`/auth/forgot-password/reset`, {
                studentId,
                email,
                otp,
                newPassword
            });

            setSuccess('Password reset successful! Redirecting to login...');
            clearStoredAuth();
            setTimeout(() => navigate('/login'), 2000);
        } catch (err) {
            setError(err.response?.data?.errorMessage || err.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderStep = () => {
        switch (step) {
            case 1:
                return (
                    <form onSubmit={handleEmailSubmit} autoComplete="off">
                        <FormControl required>
                            <FormLabel>StudentId</FormLabel>
                            <Input
                                type="text"
                                name="studentid-reset"
                                autoComplete="off"
                                value={studentId}
                                onChange={(e) => setStudentId(e.target.value)}
                                placeholder="Your Student ID"
                                disabled={isSubmitting}
                            />
                            <FormLabel>Email Address</FormLabel>
                            <Input
                                type="email"
                                name="email-reset"
                                autoComplete="off"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="your email id given while registration."
                                disabled={isSubmitting}
                            />
                        </FormControl>
                        <Button
                            type="submit"
                            loading={isSubmitting}
                            fullWidth
                            sx={{
                                mt: 2,
                                bgcolor: '#ea7a23',
                                '&:hover': { bgcolor: '#d06a1e' }
                            }}
                        >
                            Send OTP
                        </Button>
                    </form>
                );

            case 2:
                return (
                    <form onSubmit={handleOtpSubmit}>
                        <FormControl required>
                            <FormLabel>Enter OTP</FormLabel>
                            <Input
                                type="text"
                                value={otp}
                                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                placeholder="Enter 6-digit OTP"
                                disabled={isSubmitting}
                            />
                            <Typography level="body-sm" sx={{ mt: 1, color: 'neutral.500' }}>
                                OTP has been sent to {email}
                            </Typography>
                        </FormControl>
                        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                            <Button
                                variant="outlined"
                                color="neutral"
                                fullWidth
                                onClick={() => setStep(1)}
                                disabled={isSubmitting}
                            >
                                Back
                            </Button>
                            <Button
                                type="submit"
                                fullWidth
                                disabled={otp.length !== 6 || isSubmitting}
                                sx={{
                                    bgcolor: '#ea7a23',
                                    '&:hover': { bgcolor: '#d06a1e' }
                                }}
                            >
                                Verify OTP
                            </Button>
                        </Stack>
                    </form>
                );

            case 3:
                return (
                    <form onSubmit={handlePasswordSubmit}>
                        <Stack spacing={2}>
                            <FormControl required>
                                <FormLabel>New Password</FormLabel>
                                <Input
                                    type="password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    placeholder="Enter new password"
                                    disabled={isSubmitting}
                                />
                            </FormControl>
                            <FormControl required>
                                <FormLabel>Confirm Password</FormLabel>
                                <Input
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder="Confirm new password"
                                    disabled={isSubmitting}
                                />
                            </FormControl>
                            <Stack direction="row" spacing={1}>
                                <Button
                                    variant="outlined"
                                    color="neutral"
                                    fullWidth
                                    onClick={() => setStep(2)}
                                    disabled={isSubmitting}
                                >
                                    Back
                                </Button>
                                <Button
                                    type="submit"
                                    loading={isSubmitting}
                                    fullWidth
                                    sx={{
                                        bgcolor: '#ea7a23',
                                        '&:hover': { bgcolor: '#d06a1e' }
                                    }}
                                >
                                    Reset Password
                                </Button>
                            </Stack>
                        </Stack>
                    </form>
                );
            default:
                return null;
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: 'auto',
                minHeight: 'auto',
                alignItems: 'center',
                justifyContent: 'center',
                py: '20px',
                p: 2
            }}
        >
            <Sheet
                variant="outlined"
                sx={{
                    width: '100%',
                    maxWidth: 400,
                    mx: 'auto',
                    p: 3,
                    borderRadius: 'md',
                    boxShadow: 'md'
                }}
            >
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                    <Logo clickable={false} width={250} height={83} />
                </Box>

                <Typography
                    level="h4"
                    component="h1"
                    sx={{ mb: 2, textAlign: 'center' }}
                >
                    Reset Password
                </Typography>

                {error && (
                    <Alert
                        color="danger"
                        variant="soft"
                        sx={{ mb: 2 }}
                        onClose={() => setError('')}
                    >
                        {error}
                    </Alert>
                )}

                {success && (
                    <Alert
                        color="success"
                        variant="soft"
                        sx={{ mb: 2 }}
                        onClose={() => setSuccess('')}
                    >
                        {success}
                    </Alert>
                )}

                {renderStep()}

                <Button
                    variant="plain"
                    color="neutral"
                    fullWidth
                    type="button"
                    onClick={() => {
                        clearStoredAuth();
                        navigate('/login');
                    }}
                    sx={{ mt: 2 }}
                >
                    Back to Login
                </Button>
            </Sheet>
        </Box>
    );
};

export default ForgotPassword; 